import React, { useState, useEffect } from 'react';
import { X, HelpCircle, ToggleLeft, ToggleRight, ChevronRight } from 'lucide-react';
import api from '../../api/axios';
import { useToast } from '../Toast';
import { filterPhone, isValidEmail } from '../../utils/inputValidation';
import { getPlaceholders } from '../../utils/placeholders';

const ALL_RECURRENCE = [
  { label: '1 semaine',   value: 7   },
  { label: '2 semaines',  value: 14  },
  { label: '3 semaines',  value: 21  },
  { label: '4 semaines',  value: 28  },
  { label: '5 semaines',  value: 35  },
  { label: '1 mois',      value: 30  },
  { label: '2 mois',      value: 60  },
  { label: '3 mois',      value: 90  },
  { label: 'Personnalisé', value: 'custom' },
];

function Tooltip({ text }) {
  return (
    <span className="group relative cursor-default inline-flex">
      <HelpCircle size={12} className="text-slate-300" />
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 bg-slate-800 text-white text-xs rounded-xl px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity z-50 leading-relaxed text-center shadow-xl">
        {text}
      </span>
    </span>
  );
}

function Label({ children, hint }) {
  return (
    <div className="flex items-center gap-1.5 mb-1.5">
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{children}</label>
      {hint && <Tooltip text={hint} />}
    </div>
  );
}

function Input({ className = '', ...props }) {
  return (
    <input {...props}
      className={`w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition ${className}`} />
  );
}

function Select({ children, ...props }) {
  return (
    <select {...props}
      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition">
      {children}
    </select>
  );
}

function Section({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-100 rounded-2xl overflow-hidden">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 text-left">
        <span className="text-xs font-bold uppercase tracking-widest text-slate-400">{title}</span>
        <ChevronRight size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && <div className="px-4 py-4 space-y-3.5">{children}</div>}
    </div>
  );
}

const DEFAULT_ALLOWED = [7, 14, 21, 28, 35, 30, 60, 90];

export default function ClientDrawer({ client, services, allowedFrequencies, devise, onClose, onSaved }) {
  const showToast = useToast();
  const isEdit    = !!client?._id;
  const [saving,  setSaving]  = useState(false);

  const allowed   = allowedFrequencies?.length ? allowedFrequencies : DEFAULT_ALLOWED;
  const RECURRENCE = ALL_RECURRENCE.filter(o => o.value === 'custom' || allowed.includes(o.value));
  const knownVals  = RECURRENCE.map(o => o.value).filter(v => v !== 'custom');

  const init = () => ({
    nom:                client?.nom || '',
    prenom:             client?.prenom || '',
    email:              client?.email || '',
    telephone:          client?.telephone || '',
    notes:              client?.notes || '',
    preferredServiceId: client?.preferredServiceId?._id || client?.preferredServiceId || '',
    recurrenceInterval: client?.recurrenceInterval ?? '',
    reminderDaysBefore: client?.reminderDaysBefore ?? 2,
    reminderEnabled:    client?.reminderEnabled ?? false,
    lastVisitDate:      client?.lastVisitDate ? new Date(client.lastVisitDate).toISOString().slice(0,10) : '',
  });

  const [f, setF] = useState(init);
  useEffect(() => setF(init()), [client]); // eslint-disable-line

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const selectVal = f.recurrenceInterval === ''
    ? ''
    : knownVals.includes(Number(f.recurrenceInterval)) ? Number(f.recurrenceInterval) : 'custom';

  const hasRecurrence = f.recurrenceInterval !== '' && f.recurrenceInterval !== null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!f.nom.trim()) return showToast('Le nom est requis', 'error');
    if (f.email && !isValidEmail(f.email)) return showToast('Adresse email invalide', 'error');
    if (f.telephone && !/^[\d\s+\-().]+$/.test(f.telephone)) return showToast('Numéro de téléphone invalide', 'error');
    setSaving(true);
    try {
      const payload = {
        ...f,
        recurrenceInterval: hasRecurrence ? Number(f.recurrenceInterval) : null,
        reminderDaysBefore: Number(f.reminderDaysBefore),
        preferredServiceId: f.preferredServiceId || null,
        lastVisitDate:      f.lastVisitDate || null,
      };
      isEdit
        ? await api.put(`/clients/${client._id}`, payload)
        : await api.post('/clients', payload);
      showToast(isEdit ? 'Client mis à jour' : 'Client créé', 'success');
      onSaved();
    } catch (err) {
      showToast(err.response?.data?.message || 'Erreur serveur', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="w-full max-w-[420px] bg-white flex flex-col shadow-2xl border-l border-slate-200">

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100 flex-shrink-0">
          <div>
            <h2 className="font-bold text-base text-slate-900">
              {isEdit ? 'Modifier le client' : 'Nouveau client'}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
              {isEdit
                ? 'Mettez à jour le profil et les préférences de suivi'
                : 'Renseignez les informations du client pour activer son suivi'}
            </p>
          </div>
          <button onClick={onClose}
            className="p-1.5 rounded-xl hover:bg-slate-100 text-slate-400 transition mt-0.5">
            <X size={17} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-4">

            {/* Identité */}
            <Section title="Identité" defaultOpen>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Prénom</Label>
                  <Input placeholder={getPlaceholders(devise).prenom} value={f.prenom} onChange={e => set('prenom', e.target.value)} />
                </div>
                <div>
                  <Label>Nom *</Label>
                  <Input placeholder={getPlaceholders(devise).nom} required value={f.nom} onChange={e => set('nom', e.target.value)} />
                </div>
              </div>
              <div>
                <Label hint="L'email est indispensable pour envoyer les rappels automatiques">Email</Label>
                <Input type="email" placeholder="aissatou@email.com" value={f.email} onChange={e => set('email', e.target.value)} />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input placeholder={getPlaceholders(devise).telephone} value={f.telephone} onChange={e => set('telephone', filterPhone(e.target.value))} />
              </div>
              {services.length > 0 && (
                <div>
                  <Label hint="Le service que ce client commande le plus souvent">Service préféré</Label>
                  <Select value={f.preferredServiceId} onChange={e => set('preferredServiceId', e.target.value)}>
                    <option value="">- Aucun -</option>
                    {services.map(s => <option key={s._id} value={s._id}>{s.nom}</option>)}
                  </Select>
                </div>
              )}
              <div>
                <Label>Notes internes</Label>
                <textarea rows={2} placeholder="Préférences, allergie, remarques…"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm placeholder-slate-300 text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition resize-none"
                  value={f.notes} onChange={e => set('notes', e.target.value)} />
              </div>
            </Section>

            {/* Suivi */}
            <Section title="Suivi & Rappels" defaultOpen={isEdit}>
              <div>
                <Label hint="À quelle fréquence ce client revient habituellement">Fréquence de visite</Label>
                <Select value={selectVal} onChange={e => {
                  const v = e.target.value;
                  set('recurrenceInterval', v === '' ? '' : v === 'custom' ? '' : Number(v));
                }}>
                  <option value="">- Pas de suivi -</option>
                  {RECURRENCE.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
              </div>

              {selectVal === 'custom' && (
                <div>
                  <Label>Nombre de jours entre chaque visite</Label>
                  <Input type="number" min={1} placeholder="ex: 21" value={f.recurrenceInterval}
                    onChange={e => set('recurrenceInterval', e.target.value)} />
                </div>
              )}

              {hasRecurrence && (
                <>
                  <div>
                    <Label hint="Sert à calculer quand envoyer le prochain rappel">Dernière visite</Label>
                    <Input type="date" value={f.lastVisitDate} onChange={e => set('lastVisitDate', e.target.value)} />
                  </div>
                  <div>
                    <Label hint="Ex : 2 = le rappel part 2 jours avant le passage estimé">Envoyer le rappel X jours avant</Label>
                    <Input type="number" min={0} max={30} value={f.reminderDaysBefore}
                      onChange={e => set('reminderDaysBefore', e.target.value)} />
                  </div>

                  {/* Toggle rappels */}
                  <button type="button" onClick={() => set('reminderEnabled', !f.reminderEnabled)}
                    className={`w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl border-2 transition-all text-left
                      ${f.reminderEnabled
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-slate-200 bg-slate-50'}`}>
                    {f.reminderEnabled
                      ? <ToggleRight size={24} className="text-indigo-600 flex-shrink-0" />
                      : <ToggleLeft  size={24} className="text-slate-300 flex-shrink-0" />}
                    <div>
                      <p className={`text-sm font-semibold leading-none ${f.reminderEnabled ? 'text-indigo-700' : 'text-slate-500'}`}>
                        Rappels automatiques par email
                      </p>
                      <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                        {f.reminderEnabled
                          ? 'Le client recevra un email de rappel automatique'
                          : 'Aucun email ne sera envoyé automatiquement'}
                      </p>
                    </div>
                  </button>
                </>
              )}
            </Section>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex gap-2.5 flex-shrink-0">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition">
              Annuler
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-50 transition">
              {saving ? 'Enregistrement…' : isEdit ? 'Mettre à jour' : 'Créer le client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
